import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import toast from 'react-hot-toast';

const AdminBands = () => {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [selectedBand, setSelectedBand] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const { api } = useApi();

  const fetchBands = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      
      const response = await api.get(`/admin/bands?${params}`);
      setBands(response.data.data.bands);
      setPagination(response.data.data.pagination);
    } catch (error) {
      toast.error('Failed to load bands');
      console.error('Bands error:', error);
    } finally {
      setLoading(false);
    }
  }, [api, pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchBands();
  }, [fetchBands]);

  const handleStatusChange = async (bandId, isActive) => {
    try {
      await api.put(`/admin/bands/${bandId}`, { isActive });
      toast.success('Band status updated');
      fetchBands(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update band status');
      console.error('Update error:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleViewDetails = async (bandId) => {
    try {
      const response = await api.get(`/admin/bands/${bandId}`);
      setSelectedBand(response.data.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load band details');
      console.error('Band details error:', error);
    }
  };

  const handleExportBands = () => {
    const csvContent = [
      ['Band Name', 'School', 'Director', 'Email', 'Phone', 'Price', 'Status', 'Joined Date'],
      ...bands.map(band => [
        band.bandName,
        band.schoolName,
        band.directorName,
        band.email,
        band.phone || '',
        `$${band.pricePerVideo || 0}`,
        band.isActive ? 'Active' : 'Inactive',
        new Date(band.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bandcam-bands-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Bands exported successfully');
  };

  const handleLogout = () => {
    localStorage.removeItem('bandcam_admin_token');
    delete api.defaults.headers.common['Authorization'];
    window.location.href = '/admin/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-900">BandCam Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportBands}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Export Bands
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link
              to="/admin/dashboard"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Dashboard
            </Link>
            <Link
              to="/admin/bands"
              className="text-indigo-600 border-b-2 border-indigo-600 py-2 px-1 text-sm font-medium"
            >
              Bands
            </Link>
            <Link
              to="/admin/orders"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Orders
            </Link>
            <Link
              to="/admin/schools"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Schools
            </Link>
            <Link
              to="/admin/issues"
              className="text-gray-500 hover:text-gray-700 py-2 px-1 text-sm font-medium"
            >
              Issues
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <form onSubmit={handleSearch} className="flex items-end space-x-4">
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                  Search Bands
                </label>
                <input
                  type="text"
                  id="search"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search by band name, school, or director..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Search
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilters({ status: '', search: '' });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Clear
              </button>
            </form>
          </div>
        </div>

        {/* Bands Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Bands ({pagination.total})
              </h3>
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} bands
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Band</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Director</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bands.map((band) => (
                    <tr key={band.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {band.bandName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {band.schoolName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {band.directorName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {band.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ${band.pricePerVideo || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          band.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {band.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(band.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleViewDetails(band.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleStatusChange(band.id, !band.isActive)}
                          className={`${
                            band.isActive 
                              ? 'text-red-600 hover:text-red-900' 
                              : 'text-green-600 hover:text-green-900'
                          }`}
                        >
                          {band.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex items-center justify-between mt-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.limit >= pagination.total}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                      <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page * pagination.limit >= pagination.total}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Band Details Modal */}
      {showDetails && selectedBand && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Band Details</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900">Band Information</h4>
                  <p><span className="font-medium">Name:</span> {selectedBand.band.bandName}</p>
                  <p><span className="font-medium">School:</span> {selectedBand.band.schoolName}</p>
                  <p><span className="font-medium">Director:</span> {selectedBand.band.directorName}</p>
                  <p><span className="font-medium">Email:</span> {selectedBand.band.email}</p>
                  <p><span className="font-medium">Phone:</span> {selectedBand.band.phone || 'N/A'}</p>
                  <p><span className="font-medium">Price per Video:</span> ${selectedBand.band.pricePerVideo || 0}</p>
                  <p><span className="font-medium">Status:</span> {selectedBand.band.isActive ? 'Active' : 'Inactive'}</p>
                  <p><span className="font-medium">Joined:</span> {new Date(selectedBand.band.createdAt).toLocaleDateString()}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Statistics</h4>
                  <p><span className="font-medium">Total Orders:</span> {selectedBand.stats.totalOrders}</p>
                  <p><span className="font-medium">Completed Orders:</span> {selectedBand.stats.completedOrders}</p>
                  <p><span className="font-medium">Total Revenue:</span> ${selectedBand.stats.totalRevenue.toFixed(2)}</p>
                  <p><span className="font-medium">Total Commission:</span> ${selectedBand.stats.totalCommission.toFixed(2)}</p>
                </div>
              </div>

              {selectedBand.band.description && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900">Description</h4>
                  <p className="text-gray-600">{selectedBand.band.description}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBands; 